/** Page size for `site.funnels` pagination when loading import dropdown data. */
export const POPUP_IMPORT_FUNNELS_PAGE_SIZE = 120;

/** Hard cap on funnel list fetch iterations (page size × this = max funnels loaded). */
export const POPUP_IMPORT_FUNNELS_MAX_PAGES = 100;

export type PopupImportFunnelPageOption = {
  /** Relay global id (always present from GraphQL); import UI + `importPopups` `verification.pageId`. */
  id: string;
  /** Site-facing opaque id; optional match when resolving CSV / pasted ids. */
  encryptedId: string | null;
  url: string;
  name: string;
};

/** Dropdown value + `verification.pageId` on import mutation: Relay global id. */
export function pageImportPickerValue(p: PopupImportFunnelPageOption): string {
  return (p.id ?? "").trim();
}

/** Default verification page when a funnel is selected: landing page, else first page. */
export function findLandingPageForFunnel(
  funnel: PopupImportFunnelOption | undefined
): PopupImportFunnelPageOption | undefined {
  if (!funnel) return undefined;
  const landingId = (funnel.landingPageId ?? "").trim();
  if (landingId) {
    const byId = funnel.pages.find(
      (p) => pageImportPickerValue(p) === landingId
    );
    if (byId) return byId;
  }
  return funnel.pages[0];
}

export type PopupImportFunnelOption = {
  id: string;
  slug: string;
  name: string;
  /** Landing page Relay id for `addSourceTag` (入稿用リンク registration). */
  landingPageId: string | null;
  pages: PopupImportFunnelPageOption[];
};

type PageLike =
  | {
      readonly id?: string | null;
      readonly encryptedId?: string | null;
      readonly url: string;
      readonly name: string;
    }
  | null
  | undefined;

function dedupePagesByUrl(
  pages: PopupImportFunnelPageOption[]
): PopupImportFunnelPageOption[] {
  const seen = new Set<string>();
  const out: PopupImportFunnelPageOption[] = [];
  for (const p of pages) {
    const u = (p.url ?? "").trim();
    const id = (p.id ?? "").trim();
    if (!u || !id || seen.has(u)) continue;
    seen.add(u);
    const enc = (p.encryptedId ?? "").trim();
    out.push({
      url: u,
      name: (p.name ?? "").trim() || u,
      id,
      encryptedId: enc || null,
    });
  }
  return out;
}

function collectPagesFromFunnelNode(funnel: {
  readonly landingPage?: PageLike;
  readonly formPage?: readonly PageLike[] | null;
  readonly confirmPage?: PageLike;
  readonly thanksPage?: PageLike;
}): PopupImportFunnelPageOption[] {
  const raw: PopupImportFunnelPageOption[] = [];
  const push = (p: PageLike) => {
    if (p?.url) {
      const enc = (p.encryptedId ?? "").trim();
      raw.push({
        url: p.url,
        name: p.name ?? "",
        id: (p?.id ?? "").trim(),
        encryptedId: enc || null,
      });
    }
  };
  push(funnel.landingPage);
  (funnel.formPage ?? []).forEach((p) => push(p));
  push(funnel.confirmPage);
  push(funnel.thanksPage);
  return dedupePagesByUrl(raw);
}

/** Build funnel + page options from Relay `site.funnels.edges[].node` shapes. */
export function buildPopupImportFunnelOptions(
  funnelNodes: ReadonlyArray<{
    readonly id: string;
    readonly slug: string;
    readonly name: string;
    readonly landingPage?: PageLike;
    readonly formPage?: readonly PageLike[] | null;
    readonly confirmPage?: PageLike;
    readonly thanksPage?: PageLike;
  }>
): PopupImportFunnelOption[] {
  return funnelNodes.map((n) => {
    const landingPageId = (n.landingPage?.id ?? "").trim() || null;
    return {
      id: n.id,
      slug: n.slug,
      name: n.name,
      landingPageId,
      pages: collectPagesFromFunnelNode(n),
    };
  });
}

/** Normalize CSV / form strings (BOM, quotes, URI encoding, whitespace). */
export function normalizeImportString(s: string | undefined | null): string {
  if (s == null) return "";
  let t = String(s)
    .replace(/^\uFEFF/, "")
    .trim();
  if (
    (t.startsWith('"') && t.endsWith('"')) ||
    (t.startsWith("'") && t.endsWith("'"))
  ) {
    t = t.slice(1, -1).trim();
  }
  try {
    const decoded = decodeURIComponent(t);
    if (decoded !== t) t = decoded.trim();
  } catch {
    // ignore malformed URI sequences
  }
  return t.trim();
}

function padBase64ForAtob(input: string): string {
  const base = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = base.length % 4;
  if (pad === 0) return base;
  return base + "=".repeat(4 - pad);
}

/**
 * If `opaque` is a Relay-style opaque funnel id, returns the inner segment used
 * elsewhere in the app (see FunnelReportGraph: atob(id).substring("Funnel-".length)).
 */
export function tryDecodeRelayFunnelLocalId(opaque: string): string | null {
  const t = opaque.trim();
  if (t.length < 4) return null;
  let decoded: string;
  try {
    decoded = atob(padBase64ForAtob(t));
  } catch {
    return null;
  }
  if (decoded.startsWith("Funnel-")) {
    return decoded.substring("Funnel-".length);
  }
  if (decoded.startsWith("Funnel:")) {
    return decoded.substring("Funnel:".length);
  }
  try {
    const j = JSON.parse(decoded) as {
      type?: string;
      __typename?: string;
      id?: unknown;
    };
    if (j?.id == null) return null;
    const typ = `${j.type ?? ""}${j.__typename ?? ""}`.toLowerCase();
    if (typ.includes("funnel")) return String(j.id);
  } catch {
    // not JSON
  }
  return null;
}

function comparableFunnelKeys(raw: string, slug?: string): Set<string> {
  const out = new Set<string>();
  const n = normalizeImportString(raw);
  if (n) {
    out.add(n);
    out.add(n.toLowerCase());
    const local = tryDecodeRelayFunnelLocalId(n);
    if (local) {
      out.add(local);
      out.add(local.toLowerCase());
    }
  }
  if (slug) {
    const s = normalizeImportString(slug);
    if (s) {
      out.add(s);
      out.add(s.toLowerCase());
    }
  }
  return out;
}

/**
 * Match a funnel id from CSV/import to a funnel loaded from GraphQL (Relay global id, etc.).
 */
export function findFunnelOptionByImportedId(
  rawId: string,
  options: PopupImportFunnelOption[]
): PopupImportFunnelOption | undefined {
  const n = normalizeImportString(rawId);
  if (!n) return undefined;

  let hit = options.find((o) => o.id === n);
  if (hit) return hit;

  hit = options.find((o) => normalizeImportString(o.id) === n);
  if (hit) return hit;

  const nLower = n.toLowerCase();
  hit = options.find((o) => o.id.toLowerCase() === nLower);
  if (hit) return hit;

  const importKeys = comparableFunnelKeys(rawId);

  hit = options.find((o) => {
    const optionKeys = comparableFunnelKeys(o.id, o.slug);
    for (const ik of Array.from(importKeys)) {
      if (!ik) continue;
      for (const ok of Array.from(optionKeys)) {
        if (ik === ok) return true;
      }
    }
    return false;
  });
  if (hit) return hit;

  return undefined;
}

/**
 * Whether an imported URL cell matches `page.url` from GraphQL (absolute URL,
 * scheme/host differences, trailing slashes, root-relative vs absolute).
 */
export function pageUrlsMatch(
  storedPageUrl: string,
  importedRaw: string
): boolean {
  const pathKey = (u: string): string => {
    const n = normalizeImportString(u).trim();
    if (!n) return "";
    try {
      if (/^[a-z][a-z0-9+.-]*:\/\//i.test(n)) {
        const parsed = new URL(n);
        let pathname = parsed.pathname.replace(/\/+$/, "");
        if (!pathname) pathname = "/";
        return pathname;
      }
    } catch {
      // fall through to path-only handling
    }
    let path = n.split("?")[0].split("#")[0].trim();
    if (!path.startsWith("/")) path = `/${path}`;
    path = path.replace(/\/+$/, "");
    return path || "/";
  };

  const a = pathKey(storedPageUrl);
  const b = pathKey(importedRaw);
  if (!a || !b) return false;
  if (a === b) return true;
  return (
    normalizeImportString(storedPageUrl) === normalizeImportString(importedRaw)
  );
}

export function funnelPageUrlsContain(
  options: PopupImportFunnelOption[],
  funnelId: string,
  rawPageUrl: string
): boolean {
  const f =
    options.find((o) => o.id === funnelId) ??
    findFunnelOptionByImportedId(funnelId, options);
  if (!f) return false;
  const p = normalizeImportString(rawPageUrl);
  if (!p) return false;
  return f.pages.some(
    (page) =>
      pageUrlsMatch(page.url, rawPageUrl) ||
      normalizeImportString(page.name) === p
  );
}

/** Resolve a funnel page by URL (normalized), for the canonical funnel id or imported funnel key. */
export function findPageOptionByUrlForFunnel(
  options: PopupImportFunnelOption[],
  funnelId: string,
  rawPageUrl: string
): PopupImportFunnelPageOption | undefined {
  const f =
    options.find((o) => o.id === funnelId) ??
    findFunnelOptionByImportedId(funnelId, options);
  if (!f) return undefined;
  const norm = normalizeImportString(rawPageUrl);
  const trim = rawPageUrl.trim();
  if (!norm && !trim) return undefined;
  const byUrl = f.pages.find((p) => pageUrlsMatch(p.url, rawPageUrl));
  if (byUrl) return byUrl;
  // Some exports put the page display name in the URL column instead of a full URL.
  return f.pages.find((p) => normalizeImportString(p.name) === norm);
}

/** Resolve a funnel page by Relay global id, or legacy encrypted id match. */
export function findPageOptionByIdForFunnel(
  options: PopupImportFunnelOption[],
  funnelId: string,
  rawPageId: string
): PopupImportFunnelPageOption | undefined {
  const f =
    options.find((o) => o.id === funnelId) ??
    findFunnelOptionByImportedId(funnelId, options);
  if (!f || !rawPageId.trim()) return undefined;
  const idTrim = rawPageId.trim();
  return f.pages.find(
    (p) =>
      pageImportPickerValue(p) === idTrim ||
      (p.encryptedId ?? "").trim() === idTrim
  );
}
